import axios from 'axios';
import { setSyncStatus } from './syncStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true
});

// Attach Bearer token from localStorage for cross-domain auth (Safari/ITP)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Capture sync state from every successful GET so the UI can show
// "Synced 2:14pm" / "Live" indicators. We mark a response as `fromCache`
// when the browser is offline (the SW served it from disk), otherwise
// it's a fresh network call. This avoids the CORS-exposed-headers
// limitation where custom SW-injected headers may not reach JS.
api.interceptors.response.use((response) => {
  if (response.config?.method?.toLowerCase() === 'get') {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    setSyncStatus({ lastSyncAt: Date.now(), fromCache: offline });
  }
  return response;
}, (error) => Promise.reject(error));

export const organizationAPI = {
  create: (data) => api.post('/organizations', data),
  list: () => api.get('/organizations'),
  get: (id) => api.get(`/organizations/${id}`),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  listPublic: () => api.get('/organizations/public'),
  getMembers: (orgId) => api.get(`/organizations/${orgId}/members`),
  removeMember: (orgId, memberId) => api.delete(`/organizations/${orgId}/members/${memberId}`),
  addMember: (orgId, email) => api.post(`/organizations/${orgId}/members/add`, { email }),
  bulkUploadMembers: (orgId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/organizations/${orgId}/members/bulk`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getLeaderboard: (orgId) => api.get(`/organizations/${orgId}/leaderboard`),
  joinOrg: (orgId) => api.post(`/organizations/${orgId}/members`),
  myMemberships: () => api.get('/riders/my-memberships')
};

export const showAPI = {
  create: (data) => api.post('/shows', data),
  list: (state) => api.get('/shows', { params: { state } }),
  get: (id) => api.get(`/shows/${id}`),
  updateState: (id, state) => api.patch(`/shows/${id}/state`, null, { params: { state } }),
  update: (id, data) => api.put(`/shows/${id}`, data),
  delete: (id) => api.delete(`/shows/${id}`),
  updatePayback: (id, payback_percent) => api.patch(`/shows/${id}/payback`, { payback_percent }),
  updateClassPayback: (id, classId, payback_percent) => api.patch(`/shows/${id}/classes/${classId}/payback`, { payback_percent }),
  updateClassPayoutConfig: (id, classId, config) => api.patch(`/shows/${id}/classes/${classId}/payout-config`, config),
};

export const templateAPI = {
  list: () => api.get('/show-templates'),
  save: (data) => api.post('/show-templates', data),
  delete: (id) => api.delete(`/show-templates/${id}`)
};

export const averageAPI = {
  list: (showId) => api.get(`/shows/${showId}/averages`),
  create: (showId, data) => api.post(`/shows/${showId}/averages`, data),
  update: (showId, avgId, data) => api.patch(`/shows/${showId}/averages/${avgId}`, data),
  delete: (showId, avgId) => api.delete(`/shows/${showId}/averages/${avgId}`),
  results: (showId, avgId) => api.get(`/shows/${showId}/averages/${avgId}/results`),
};

export const targetAPI = {
  list: (showId) => api.get(`/shows/${showId}/targets`),
  create: (showId, data) => api.post(`/shows/${showId}/targets`, data),
  update: (showId, tgtId, data) => api.patch(`/shows/${showId}/targets/${tgtId}`, data),
  delete: (showId, tgtId) => api.delete(`/shows/${showId}/targets/${tgtId}`),
  results: (showId, tgtId) => api.get(`/shows/${showId}/targets/${tgtId}/results`),
  optIn: (showId, tgtId, data) => api.post(`/shows/${showId}/targets/${tgtId}/opt-in`, data || {}),
  listOptIns: (showId, tgtId) => api.get(`/shows/${showId}/targets/${tgtId}/opt-ins`),
  updateOptIn: (showId, tgtId, optInId, payment_status) =>
    api.patch(`/shows/${showId}/targets/${tgtId}/opt-ins/${optInId}`, { payment_status }),
  patchOptIn: (showId, tgtId, optInId, patch) =>
    api.patch(`/shows/${showId}/targets/${tgtId}/opt-ins/${optInId}`, patch),
  backfillSuggestions: (showId, tgtId) =>
    api.get(`/shows/${showId}/targets/${tgtId}/backfill-suggestions`),
  removeOptIn: (showId, tgtId, optInId) => api.delete(`/shows/${showId}/targets/${tgtId}/opt-ins/${optInId}`),
};

export const subscriptionAPI = {
  plans: () => api.get('/subscriptions/plans'),
  subscribe: (planId) => api.post('/subscriptions/subscribe', { plan_id: planId }),
  my: () => api.get('/subscriptions/my'),
  cancel: (subId) => api.post('/subscriptions/cancel', { subscription_id: subId }),
  adminAll: () => api.get('/subscriptions/admin/all'),
  adminUpdate: (subId, status) => api.put(`/subscriptions/admin/${subId}`, { status }),
  adminGrant: (userId, planId) => api.post('/subscriptions/admin/grant', { user_id: userId, plan_id: planId }),
  adminGrantBulk: (userIds, planId) => api.post('/subscriptions/admin/grant-bulk', { user_ids: userIds, plan_id: planId }),
  adminDelete: (subId) => api.delete(`/subscriptions/admin/${subId}`),
  adminPlans: () => api.get('/subscriptions/admin/plans'),
  adminUpdatePlan: (planId, data) => api.put(`/subscriptions/admin/plans/${planId}`, data),
  adminUsers: () => api.get('/subscriptions/admin/users'),
};

export const platformAPI = {
  getSettings: () => api.get('/admin/platform-settings'),
  updateSettings: (data) => api.put('/admin/platform-settings', data),
  listUsers: (q = '') => api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  listOrgs: () => api.get('/admin/organizations'),
  deleteOrganization: (orgId) => api.delete(`/admin/organizations/${orgId}`),
  lmasterCount: () => api.get('/admin/lmaster-count'),
};

export const classEntryAPI = {
  add: (showId, classId, data) => api.post(`/shows/${showId}/classes/${classId}/entries`, data),
  list: (showId, classId) => api.get(`/shows/${showId}/classes/${classId}/entries`),
  listAll: (showId) => api.get(`/shows/${showId}/all-entries`),
  update: (showId, classId, entryId, data) => api.put(`/shows/${showId}/classes/${classId}/entries/${entryId}`, data),
  remove: (showId, classId, entryId) => api.delete(`/shows/${showId}/classes/${classId}/entries/${entryId}`),
  generateDraw: (showId, classId) => api.post(`/shows/${showId}/classes/${classId}/draw`),
  reverseDraw: (showId, classId) => api.post(`/shows/${showId}/classes/${classId}/reverse-draw`),
  copyDraw: (showId, classId, sourceClassId, reverse) =>
    api.post(`/shows/${showId}/classes/${classId}/copy-draw`, {
      source_class_id: sourceClassId,
      reverse,
    }),
  setDrawNumber: (showId, classId, entryId, drawNumber) =>
    api.patch(`/shows/${showId}/classes/${classId}/entries/${entryId}/draw`, { draw_number: drawNumber }),
  clearDraw: (showId, classId) => api.post(`/shows/${showId}/classes/${classId}/clear-draw`),
  bulkImport: (showId, classId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/shows/${showId}/classes/${classId}/entries/bulk`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  toggleSidepot: (showId, classId, entryId, sidepotId) => api.patch(`/shows/${showId}/classes/${classId}/entries/${entryId}/sidepots?sidepot_id=${sidepotId}`),
  updatePayment: (showId, classId, entryId, status) => api.patch(`/shows/${showId}/classes/${classId}/entries/${entryId}/payment?status=${status}`),
  setClassStatus: (showId, classId, status) => api.patch(`/shows/${showId}/classes/${classId}/status`, { status }),
  rolloverEntry: (showId, classId, entryId, targetClassId, targetSidepotId = null) => api.post(`/shows/${showId}/classes/${classId}/entries/${entryId}/rollover`, { target_class_id: targetClassId, target_sidepot_id: targetSidepotId }),
  clearAll: (showId, classId) => api.delete(`/shows/${showId}/classes/${classId}/entries/all`),
  bulkImportShow: (showId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/shows/${showId}/entries/bulk`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const rolloverAPI = {
  setRolloverFrom: (showId, classId, rolloverFromClassIds, rolloverCategory) => api.patch(`/shows/${showId}/classes/${classId}/rollover`, { rollover_from_class_ids: rolloverFromClassIds || [], rollover_category: rolloverCategory }),
  getCandidates: (showId, classId) => api.get(`/shows/${showId}/classes/${classId}/rollover-candidates`),
  createEntries: (showId, classId, entryIds) => api.post(`/shows/${showId}/classes/${classId}/rollover-entries`, { entry_ids: entryIds }),
  removeEntry: (showId, classId, entryId) => api.delete(`/shows/${showId}/classes/${classId}/rollover-entries/${entryId}`),
};

export const sidepotAPI = {
  results: (showId, sidepotId) => api.get(`/shows/${showId}/sidepots/${sidepotId}/results`),
  calculatePayouts: (sidepotId, data) => api.post(`/sidepots/${sidepotId}/calculate`, data),
  create: (showId, data) => api.post(`/shows/${showId}/sidepots`, data),
  update: (showId, sidepotId, data) => api.patch(`/shows/${showId}/sidepots/${sidepotId}`, data),
  delete: (showId, sidepotId) => api.delete(`/shows/${showId}/sidepots/${sidepotId}`),
  // Remove a single rider's enrollment from a sidepot. `refund` is
  // echoed back to the toast copy — actual money movement is recorded
  // via the rider-charges flow if needed.
  removeEntry: (showId, sidepotId, entryId, refund = false) =>
    api.delete(`/shows/${showId}/sidepots/${sidepotId}/entries/${entryId}`, {
      params: { refund: refund ? 'true' : 'false' },
    }),
};

export const entryAPI = {
  create: (data) => api.post('/entries', data),
  list: (showId) => api.get('/entries', { params: { show_id: showId } })
};

export const drawAPI = {
  generate: (data) => api.post('/draws/generate', data),
  get: (classId) => api.get(`/draws/${classId}`)
};

export const scoreAPI = {
  submit: (data) => api.post('/scores', data)
};

export const editorAPI = {
  list: (orgId) => api.get(`/organizations/${orgId}/editors`),
  add: (orgId, email) => api.post(`/organizations/${orgId}/editors`, { email }),
  remove: (orgId, editorId) => api.delete(`/organizations/${orgId}/editors/${editorId}`),
};


export const resultsAPI = {
  get: (classId) => api.get(`/results/${classId}`),
  getLive: (classId) => api.get(`/results/${classId}/live`),
  publish: (classId) => api.post(`/results/${classId}/publish`),
  getPublished: (classId) => api.get(`/results/${classId}/published`),
  getShowPublished: (showId) => api.get(`/shows/${showId}/published-results`),
};

export const payoutAPI = {
  calculate: (classId, data) => api.post(`/payouts/calculate/${classId}`, data),
  get: (classId) => api.get(`/payouts/${classId}`)
};

export const paymentAPI = {
  createCheckout: (entryId, originUrl) => api.post('/payments/square/checkout', null, { params: { entry_id: entryId, origin_url: originUrl } }),
  checkStatus: (sessionId) => api.get(`/payments/square/status/${sessionId}`)
};

export const reservationAPI = {
  getForShow: (showId) => api.get(`/shows/${showId}/reservations`),
  getMyReservations: () => api.get('/riders/my-reservations')
};

export const riderAPI = {
  myEntriesByShow: () => api.get('/riders/my-entries'),
  checkoutShow: (showId, originUrl) => api.post('/payments/square/checkout-show', { show_id: showId, origin_url: originUrl }),
  getHistory: () => api.get('/riders/history'),
  getStats: () => api.get('/riders/stats'),
  getManagedRiders: () => api.get('/riders/managed-riders'),
  addManagedRider: (data) => api.post('/riders/managed-riders', data),
  deleteManagedRider: (id) => api.delete(`/riders/managed-riders/${id}`),
  getHorses: () => api.get('/riders/horses'),
  addHorse: (data) => api.post('/riders/horses', data),
  deleteHorse: (id) => api.delete(`/riders/horses/${id}`),
  quickEnterProfile: () => api.get('/riders/quick-enter-profile'),
};

export const refundAPI = {
  request: (showId, reason) => api.post('/refunds/request', { show_id: showId, reason }),
  myRequests: () => api.get('/refunds/my-requests'),
  listForShow: (showId) => api.get(`/refunds/requests/${showId}`),
  approve: (requestId) => api.post(`/refunds/approve/${requestId}`),
  deny: (requestId, reason) => api.post(`/refunds/deny/${requestId}`, { reason }),
  direct: (showId, riderId) => api.post('/refunds/direct', { show_id: showId, rider_id: riderId }),
};

export const seriesAPI = {
  create: (data) => api.post('/series', data),
  list: () => api.get('/series'),
  listPublic: () => api.get('/series/public'),
  get: (id) => api.get(`/series/${id}`),
  update: (id, data) => api.patch(`/series/${id}`, data),
  addShow: (seriesId, showId) => api.post(`/series/${seriesId}/shows`, { show_id: showId }),
  removeShow: (seriesId, showId) => api.delete(`/series/${seriesId}/shows/${showId}`),
  getStandings: (id) => api.get(`/series/${id}/standings`)
};

export const auditAPI = {
  list: (showId, opts = {}) => {
    const params = {};
    if (opts.limit) params.limit = opts.limit;
    if (opts.actionPrefix) params.action_prefix = opts.actionPrefix;
    return api.get(`/shows/${showId}/audit-log`, { params });
  },
};

export default api;
